import {Helmet} from '@dr.pogodin/react-helmet';
import {skipToken} from '@reduxjs/toolkit/query';
import {isFeatureEnabled} from 'feature-flags';
import {AlertProps, openConfirmDialog, showNotification} from 'platform/components';
import {Show} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';
import {match} from 'ts-pattern';

import {useEffect, useState} from 'react';
// eslint-disable-next-line no-restricted-imports
import {useSearchParams} from 'react-router-dom';

import {always, defaultTo, isNil, isNotNil} from 'ramda';
import {concatAll, isNonEmptyArray, isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetCurrentUserInfoQuery} from '@dms/api/customer';
import {
  useGetRecordHeaderCollapsedQuery,
  useSetRecordHeaderCollapsedMutation,
} from '@dms/api/settings';
import {EntityResourceIds} from '@dms/api/shared';
import {
  useGetServiceCaseOrdersQuery,
  usePostServiceCaseActionCancelMutation,
  usePostServiceCaseActionReOpenMutation,
  usePostServiceOrderMutation,
} from '@dms/api/workshop';
import {featureFlags} from '@dms/feature-flags';
import {TelephonyCallRecordVisit} from '@dms/features/telephony-call';
import i18n from '@dms/i18n';
import {testIds, workshopRoutes} from '@dms/routes';
import {
  useTenant,
  DEFAULT_PRICE,
  DetailTemplate,
  DetailTemplateHeader,
  EntityHeaderControl,
  EntityHeaderFlag,
  ORDER_TABS,
  handleApiError,
  queryParams,
  usePermissions,
  useStateFlag,
  workshopSearchParams,
  CheckoutIssuingProvider,
  useCheckoutIssuing,
  EntityHeaderAction,
} from '@dms/shared';
import {
  ServiceCaseDetailActionsProvider,
  ServiceOrderReturnMaterialModal,
  useServiceCaseDetailActions,
} from '@dms/shared-aftersales';

import {
  Nullish,
  buildArray,
  buildObject,
  composePath,
  isURLDangerous,
  useQueryState,
  useNavigate,
} from 'shared/web';

import {SOURCE_DATA_GRID} from 'features/eag-grid';

import {IntegrationsDropdown} from '../../components/IntegrationsDropdown/IntegrationsDropdown';
import {CHECKIN_SECTION, HANDOVER_SECTION} from '../../constants/detailSections';
import {useWorkshopUrl} from '../../hooks/useWorkshopUrl';
import {useXentryIntegration} from '../../hooks/useXentryIntegration';
import {getSparePartStateFlag} from '../../utils/getSparePartStateFlag';
import {getTimeTrackingStateFlag} from '../../utils/getTimeTrackingStateFlag';
import {ServiceCaseActivityPanel} from './components/ServiceCaseActivityPanel/ServiceCaseActivityPanel';
import {
  SERVICE_CASE_CREATE_ORDER_ACTION,
  SERVICE_CASE_CANCEL_ACTION,
  SERVICE_CASE_REOPEN_ACTION,
} from './constants/actionKeys';
import {useOpenBoschFleetTermsAndConditions} from './hooks/useOpenBoschFleetTermsAndConditions';
import {useServiceCaseDetailData} from './hooks/useServiceCaseDetailData';
import {useServiceCaseDetailHandlers} from './hooks/useServiceCaseDetailHandlers';
import {useServiceCaseDetailSections} from './hooks/useServiceCaseDetailSections';
import {useServiceCaseVehicleHeaderParameters} from './hooks/useServiceCaseVehicleHeaderParameters';
import {useVehicleHistoryActionCallbacks} from './hooks/useVehicleHistoryActionCallbacks';
import {getAdditionalInformationFlags} from './utils/getAdditionalInformationFlags';
import {getMarkersFlags} from './utils/getMarkersFlags';
import {getMenuItemsFromActions} from './utils/getMenuItemsFromActions';
import {getOrdersWithMarginUnderMinimum} from './utils/getOrdersWithMarginUnderMinimum';

const openExternalSchedulerUrl = (url: string | Nullish) => {
  if (isURLDangerous(url)) {
    return;
  }
  window.open(url ?? '', '_blank', 'noopener,noreferrer');
};

export function ServiceCaseDetail() {
  return (
    <CheckoutIssuingProvider>
      <ServiceCaseDetailActionsProvider>
        <ServiceCaseDetailContent />
      </ServiceCaseDetailActionsProvider>
    </CheckoutIssuingProvider>
  );
}

// eslint-disable-next-line complexity, max-lines-per-function, max-statements -- Pre-existing violation
function ServiceCaseDetailContent() {
  const {serviceCaseId, serviceOrderReturnId, serviceOrderReturnAuthorizationProfileId} =
    useWorkshopUrl();

  const {isCheckoutIssuing} = useCheckoutIssuing();
  const formatCurrency = useFormatCurrency();
  const tenant = useTenant();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [, setOrderId] = useQueryState(queryParams.SERVICE_CASE_ORDER_ID);
  const [, setOrderTab] = useQueryState(queryParams.SERVICE_CASE_ORDER_TAB);

  const {executeAction, isAnyActionPending} = useServiceCaseDetailActions();

  const {data, isLoading, isError, isHeaderLoading} = useServiceCaseDetailData({serviceCaseId});

  // The user setting is shared by every record detail with a collapsible header, so the choice
  // carries over between service cases, direct sales, reloads and devices.
  const isCollapsibleHeaderEnabled = isFeatureEnabled(featureFlags.SERVICE_CASE_COLLAPSIBLE_HEADER);
  const {data: isHeaderCollapsed = false, isLoading: isHeaderCollapsedLoading} =
    useGetRecordHeaderCollapsedQuery(undefined, {skip: !isCollapsibleHeaderEnabled});
  const {data: currentUser} = useGetCurrentUserInfoQuery(undefined, {
    skip: !isCollapsibleHeaderEnabled,
  });
  const [setRecordHeaderCollapsed, {isLoading: isSavingHeaderCollapsed}] =
    useSetRecordHeaderCollapsedMutation();

  const handleHeaderCollapsedChange = (isCollapsed: boolean) => {
    // A click during a save is ignored: overlapping saves would roll back to a value nobody saved.
    if (isNil(currentUser) || isSavingHeaderCollapsed) {
      return;
    }

    setRecordHeaderCollapsed({userId: currentUser.id, isCollapsed}).unwrap().catch(handleApiError);
  };

  const [postServiceOrder] = usePostServiceOrderMutation();
  const [postServiceCaseActionCancel] = usePostServiceCaseActionCancelMutation();
  const [postServiceCaseActionReOpen] = usePostServiceCaseActionReOpenMutation();

  const [
    canReadChronos,
    canReadPneuhotel,
    canCreateServiceOrder,
    canReadServiceCase,
    canReadTasks,
    canCancelServiceCase,
  ] = usePermissions({
    permissionKeys: [
      'chronosUser',
      'pneuhotelRead',
      'serviceOrderCreate',
      'serviceCaseRead',
      'readTask',
      'serviceCaseCancel',
    ],
    scopes: {
      serviceCaseRead: {
        // BE Handles SC ACL
        branchId: 'SKIP',
        // BE Handles SC ACL
        serviceCaseType: 'SKIP',
      },
      serviceCaseCancel: {
        branchId: data.serviceCase?.branchId ?? '',
        serviceCaseType: data.serviceCase?.serviceCaseTypeId ?? '',
      },
    },
  });

  const {
    serviceCase,
    checkIn,
    vehicle,
    comments,
    additionalNote,
    postponedJobs,
    orderVariants,
    vehicleCustomersCount,
    operatorCustomer,
    additionalInformation,
    serviceCaseOrderCount,
    serviceCaseActions,
    recallMessage,
    itemsTotalAmount,
    pendingServiceItems,
    pendingServiceOrderReturn,
    activeServiceCampaignsLength,
    tireSetCount,
    boschFleetIntegration,
    ordersWithMarginUnderMinimumSnapshot,
  } = data;

  const hasComments = comments.length > 0;

  // Mirrors the Sections resolution — the section query param is absent on a cold load,
  // which lands on the Orders default section.
  const activeSection =
    searchParams.get(queryParams.COMPONENT_SECTIONS_SECTION) ??
    workshopSearchParams.serviceDetail.orders;
  const isOrdersSectionActive = activeSection === workshopSearchParams.serviceDetail.orders;

  // Latch, never reset: once Orders has been active the page keeps a live orders-list
  // subscription (deduped with the section's own, so free on the default landing) and the
  // margin banner follows serviceOrders invalidations instead of flip-flopping back to the
  // bootstrap snapshot on tab switches. Deep links to other sections use the snapshot until
  // Orders is first opened.
  const [hasOrdersBeenActive, setHasOrdersBeenActive] = useState(isOrdersSectionActive);
  if (isOrdersSectionActive && !hasOrdersBeenActive) {
    setHasOrdersBeenActive(true);
  }

  const serviceCaseOrdersQuery = useGetServiceCaseOrdersQuery(
    hasOrdersBeenActive ? {serviceCaseId} : skipToken
  );

  // `currentData` + the settle shape: keep the previous list through same-key refetches, but
  // never render case A's orders under case B during an in-place navigation — the bootstrap
  // snapshot covers the gap until the new case's list lands.
  const liveServiceCaseOrders = serviceCaseOrdersQuery.currentData;
  const isLiveOrdersListReady =
    isNotNil(liveServiceCaseOrders) || !serviceCaseOrdersQuery.isFetching;

  const ordersWithMarginUnderMinimum =
    hasOrdersBeenActive && isLiveOrdersListReady
      ? getOrdersWithMarginUnderMinimum(liveServiceCaseOrders)
      : ordersWithMarginUnderMinimumSnapshot;

  const hasBookingUrl = isNotNilOrEmpty(serviceCase?.bookingUrl);
  const hasRepairUrl = isNotNilOrEmpty(serviceCase?.repairUrl);

  const {
    handlePendingLabourClick,
    handlePendingMaterialClick,
    handlePendingReturnClick,
    handleServiceOrderReturnSubmit,
  } = useServiceCaseDetailHandlers({
    pendingServiceItems,
    pendingServiceOrderReturn,
    serviceCase,
  });

  const {xentryAlert} = useXentryIntegration({
    serviceCaseId,
    'data-testid': testIds.workshop.createServiceCase('xentry'),
  });

  const stateFlag = useStateFlag(serviceCase?.state);
  const ordersStateFlag = useStateFlag(serviceCase?.ordersState);
  const markersFlags = getMarkersFlags(serviceCase?.markers);
  const timeTrackingFlag = canReadChronos
    ? getTimeTrackingStateFlag(serviceCase?.timeTrackingState)
    : undefined;
  const sparePartFlag = getSparePartStateFlag(serviceCase?.sparePartWarehouseState);
  const additionalInformationFlags = getAdditionalInformationFlags(additionalInformation);

  const isCancelled = serviceCase?.state === 'CANCELLED';
  const isHandedOver = serviceCase?.state === 'HANDED_OVER';

  const vehicleHeaderParameters = useServiceCaseVehicleHeaderParameters(
    serviceCase,
    vehicle,
    checkIn,
    hasComments,
    additionalNote?.note
  );

  const {serviceRequestsActionCallback, workItemsActionCallback} = useVehicleHistoryActionCallbacks(
    {
      serviceCaseId,
      'data-testid': testIds.workshop.serviceCaseDetail('vehicleHistory'),
    }
  );

  const historyVehicleId = serviceCase?.vehicleId;

  // The service-history grid definitions carry the copy-to-service-order actions, so copying works
  // from the header dialog exactly as it does in the History of repairs section.
  const vehicleHistory = isNotNil(historyVehicleId)
    ? {
        vehicleId: historyVehicleId,
        serviceRequestsGridCode: 'service-history-order-requests',
        workItemsGridCode: 'service-history-order-items',
        serviceRequestsActionCallback,
        workItemsActionCallback,
      }
    : undefined;

  useEffect(
    function redirectDraftToCreatePage() {
      if (serviceCase?.state === 'DRAFT') {
        const searchParams = new URLSearchParams(window.location.search);
        navigate(
          composePath(workshopRoutes.draftServiceCase, {
            params: {id: serviceCaseId},
            queryParams: {
              tab: workshopSearchParams.create.serviceJobs,
              [SOURCE_DATA_GRID]: searchParams.get(SOURCE_DATA_GRID),
            },
          })
        );
      }
    },
    [serviceCase?.state] // eslint-disable-line react/exhaustive-deps -- Muted pre-existing violation
  );

  const handleNavigateCheckinHandover = (tab: string) => {
    searchParams.set(
      queryParams.COMPONENT_SECTIONS_SECTION,
      workshopSearchParams.serviceDetail.checkinHandover
    );
    searchParams.set(queryParams.SERVICE_CASE_CHECKIN_HANDOVER, tab);
    setSearchParams(searchParams);
  };

  const handleCreateOrder = (orderVariantId?: string | null) => {
    if (isNil(orderVariantId)) {
      return;
    }
    executeAction(SERVICE_CASE_CREATE_ORDER_ACTION, () =>
      postServiceOrder({serviceCaseId, body: {orderVariantId}})
        .unwrap()
        .then((order) => {
          const createdId = order?.id;
          if (isNil(createdId)) {
            return;
          }
          setOrderId(createdId);
          setOrderTab(ORDER_TABS.JOBS);
          showNotification.success(i18n.t('entity.order.labels.createSuccess'));
        })
        .catch(handleApiError)
    );
  };

  const handleActionClick = (actionKey: 'cancel' | 'reOpen' | Nullish) => {
    if (isNil(actionKey)) {
      return;
    }

    const confirmText = match(actionKey)
      .with('cancel', always(i18n.t('entity.serviceCase.labels.cancelService')))
      .with('reOpen', always(i18n.t('entity.serviceCase.labels.reOpenService')))
      .exhaustive();

    openConfirmDialog({
      text: confirmText,
      onConfirm: () => {
        match(actionKey)
          .with('cancel', () =>
            executeAction(SERVICE_CASE_CANCEL_ACTION, () =>
              postServiceCaseActionCancel({serviceCaseId: serviceCase?.id ?? ''})
                .unwrap()
                .catch(handleApiError)
            )
          )
          .with('reOpen', () =>
            executeAction(SERVICE_CASE_REOPEN_ACTION, () =>
              postServiceCaseActionReOpen({serviceCaseId: serviceCase?.id ?? ''})
                .unwrap()
                .catch(handleApiError)
            )
          )
          .exhaustive();
      },
    });
  };

  const caseActionMenuItems = getMenuItemsFromActions(
    handleActionClick,
    isAnyActionPending,
    canCancelServiceCase
  )(serviceCaseActions?.caseActions?.options);

  const sections = useServiceCaseDetailSections({
    serviceCaseId,
    activeSection,
    canCancelServiceCase,
    canReadTasks,
    canReadServiceCase,
    canReadPneuhotel,
    serviceCase,
    serviceCaseOrderCount,
    vehicleCustomersCount,
    tireSetCount,
    postponedJobs,
    activeServiceCampaignsLength,
  });

  const {isTermsAndConditionsDocumentAvailable, openTermsAndConditionsDocument} =
    useOpenBoschFleetTermsAndConditions(boschFleetIntegration);

  const headerTitle = [vehicle?.title, operatorCustomer?.displayName]
    .filter(isNotNilOrEmpty)
    .join(' - ');

  const getHeader = buildObject<DetailTemplateHeader>()
    .title(headerTitle)
    .isEntityLoading(isHeaderLoading)
    .height(38)
    .resourceId(EntityResourceIds.serviceCase)
    .parkingRecordId(defaultTo(undefined, serviceCase?.vehicleId))
    .authorizationProfileRecordId(defaultTo(undefined, serviceCase?.authorizationProfileId))
    .recordId(defaultTo(undefined, serviceCase?.id))
    .controls(
      buildArray<EntityHeaderControl>()
        .whenNot(isHandedOver, 'PARKING')
        .when(serviceCase?.authorizationProfileId, 'AUTHORIZATION_PROFILE')
        .when(isNotNil(vehicleHistory), 'VEHICLE_HISTORY')
        .add('ASSIGNEE')
    )
    .vehicleHistory(vehicleHistory)
    .icon('action/build')
    .flags(
      concatAll<EntityHeaderFlag[]>([
        buildArray<EntityHeaderFlag>()
          .when(stateFlag, stateFlag as EntityHeaderFlag)
          .when(ordersStateFlag, ordersStateFlag as EntityHeaderFlag)
          .when(isNotNil(timeTrackingFlag), timeTrackingFlag! as EntityHeaderFlag)
          .when(isNotNil(sparePartFlag), sparePartFlag! as EntityHeaderFlag),
        markersFlags,
        additionalInformationFlags,
      ])
    )
    .parameters(vehicleHeaderParameters)
    .primaryParameter(
      formatCurrency(
        defaultTo(DEFAULT_PRICE, itemsTotalAmount?.totalPrice?.withVat?.amount),
        defaultTo(tenant.tenantCurrency, itemsTotalAmount?.totalPrice?.withVat?.currency),
        2
      ) ?? '',
      Boolean(itemsTotalAmount?.totalPrice?.withVat)
    )
    .secondaryParameter(
      `${formatCurrency(
        defaultTo(DEFAULT_PRICE, itemsTotalAmount?.totalPrice?.withoutVat?.amount),
        defaultTo(tenant.tenantCurrency, itemsTotalAmount?.totalPrice?.withoutVat?.currency),
        2
      )} ${i18n.t('entity.serviceCase.labels.withoutVat')}`,
      Boolean(itemsTotalAmount?.totalPrice?.withoutVat)
    )
    .actions(
      buildArray<EntityHeaderAction>()
        .when(isNonEmptyArray(caseActionMenuItems), {
          type: 'dropdown-iconButton',
          icon: 'navigation/more_vert',
          priority: 'secondary',
          isDisabled: isAnyActionPending,
          menuItems: caseActionMenuItems,
        })
        .when(canCreateServiceOrder, {
          type: 'dropdown-button',
          variant: 'secondary',
          rightIcon: 'navigation/expand_more',
          title: i18n.t('entity.order.actions.createOrder'),
          isHeightLimited: true,
          isDisabled: isHandedOver || isCancelled || isAnyActionPending,
          menuItems:
            orderVariants?.map((variant) => ({
              label: variant?.name ?? '',
              onClick: () => handleCreateOrder(variant?.serviceOrderVariantId),
              isDisabled: isAnyActionPending,
            })) ?? [],
        })
        .whenNot(canCreateServiceOrder, {
          type: 'buttonWithTooltip',
          variant: 'secondary',
          rightIcon: 'navigation/expand_more',
          title: i18n.t('entity.order.actions.createOrder'),
          isDisabled: true,
          tooltip: {label: i18n.t('general.labels.noPermission')},
        })
        .add({
          type: 'custom',
          component: (
            <IntegrationsDropdown
              serviceCaseId={serviceCaseId}
              isExternalActionPending={isAnyActionPending}
            />
          ),
        })
        .when(hasBookingUrl, {
          type: 'button',
          variant: 'secondary',
          leftIcon: 'action/calendar_today',
          title: i18n.t('general.actions.schedule'),
          onClick: () => openExternalSchedulerUrl(serviceCase?.bookingUrl),
          isDisabled:
            (serviceCase?.state !== 'CREATED' && serviceCase?.state !== 'CHECKED_IN') ||
            isAnyActionPending,
        })
        .when(hasRepairUrl, {
          type: 'button',
          variant: 'secondary',
          leftIcon: 'action/calendar_today',
          title: i18n.t('entity.serviceScheduler.actions.planner'),
          onClick: () => openExternalSchedulerUrl(serviceCase?.repairUrl),
          isDisabled: isAnyActionPending,
        })
        .whenNot(checkIn, {
          type: 'button',
          title: i18n.t('entity.workshop.labels.checkIn'),
          variant: 'primary',
          onClick: () => handleNavigateCheckinHandover(CHECKIN_SECTION),
          isDisabled: isCancelled || isAnyActionPending,
        })
        .when(checkIn, {
          type: 'button',
          title: i18n.t('entity.workshop.labels.handover'),
          variant: isHandedOver ? 'secondary' : 'primary',
          onClick: () => handleNavigateCheckinHandover(HANDOVER_SECTION),
          isDisabled: isCancelled || isHandedOver || isAnyActionPending,
        })
    )
    .alerts(
      buildArray<AlertProps>()
        .when(recallMessage, {type: 'banner', message: recallMessage})
        .when(isNotNil(xentryAlert), xentryAlert as AlertProps)
        .when(
          isNotNilOrEmpty(pendingServiceItems?.workBasket) ||
            isNotNilOrEmpty(pendingServiceItems?.materialBasket),
          {
            type: 'banner',
            variant: 'warning',
            title: i18n.t('entity.serviceCase.labels.pendingItems'),
            hyperlinks: [
              {
                title: i18n.t('entity.serviceCase.actions.showLabour'),
                isDisabled: !isNotNilOrEmpty(pendingServiceItems?.workBasket),
                onClick: handlePendingLabourClick,
              },
              {
                title: i18n.t('entity.serviceCase.actions.showMaterial'),
                isDisabled: !isNotNilOrEmpty(pendingServiceItems?.materialBasket),
                onClick: handlePendingMaterialClick,
              },
            ],
          }
        )
        .when(isNotNil(boschFleetIntegration), {
          type: 'banner',
          variant: 'info',
          title: i18n.t('entity.customer.labels.boschFleetCustomer'),
          message: i18n.t('entity.customer.labels.boschFleetCustomerInfo'),
          hyperlinks: [
            {
              title: i18n.t('entity.customer.labels.boschFleetTermsAndConditions'),
              rightIcon: 'action/open_in_new',
              isDisabled: !isTermsAndConditionsDocumentAvailable,
              onClick: openTermsAndConditionsDocument,
            },
          ],
        })
        .when(pendingServiceOrderReturn, {
          title: i18n.t('entity.warehouse.notifications.youHavePendingItemsForReturn'),
          hyperlinks: [
            {
              title: i18n.t('entity.warehouse.actions.showPending'),
              onClick: handlePendingReturnClick,
            },
          ],
        })
        .when(isNonEmptyArray(ordersWithMarginUnderMinimum), {
          type: 'banner',
          variant: 'warning',
          message: i18n.t('entity.serviceCase.labels.marginUnderMinimumWarning', {
            orderNumbers: ordersWithMarginUnderMinimum.map((order) => order.number).join(', '),
          }),
        })
    )
    .build();

  return (
    <>
      <Helmet title={i18n.t('page.workshop.labels.serviceCase')} />
      <TelephonyCallRecordVisit
        resourceId={EntityResourceIds.serviceCase}
        recordId={serviceCaseId}
        title={vehicle?.title ?? i18n.t('page.workshop.labels.serviceCase')}
        details={[serviceCase?.number, stateFlag?.label]}
        customerName={
          operatorCustomer?.customerId === serviceCase?.customerId
            ? (operatorCustomer?.displayName ?? null)
            : null
        }
      />
      <DetailTemplate
        isFullHeight
        isLocked={isCheckoutIssuing}
        isLoading={isLoading || isHeaderCollapsedLoading}
        isError={isError}
        header={getHeader}
        sections={sections}
        headerCollapse={
          isCollapsibleHeaderEnabled
            ? {isCollapsed: isHeaderCollapsed, onCollapsedChange: handleHeaderCollapsedChange}
            : undefined
        }
        activityPanel={<ServiceCaseActivityPanel serviceCaseId={serviceCaseId} />}
        permissions={{
          tag: {
            isReadable: true,
            isCreatable: true,
            isUpdatable: true,
            isDeletable: true,
            isAssignable: true,
            isUnassignable: true,
          },
        }}
        defaultSection={workshopSearchParams.serviceDetail.orders}
        data-testid={testIds.workshop.serviceCaseDetail('page')}
      />
      <Show when={!!serviceOrderReturnId && !!serviceOrderReturnAuthorizationProfileId}>
        <ServiceOrderReturnMaterialModal
          isFromServiceOrder
          serviceOrderReturnId={serviceOrderReturnId}
          authorizationProfileId={serviceOrderReturnAuthorizationProfileId}
          onSubmit={handleServiceOrderReturnSubmit}
          data-testid={testIds.workshop.serviceCaseDetail('returnMaterialModal')}
        />
      </Show>
    </>
  );
}
